import React, { useEffect, useMemo, useState } from "react";
import {
  Building2,
  CalendarDays,
  Ellipsis,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import ConfirmDialog from "../components/ConfirmDialog";
import InstitutionLogo from "../components/InstitutionLogo";
import api from "../utils/api";
import { useEvents, useInstitutions } from "../lib/queries";
import { Button } from "../components/ui/button";
import AppSelect from "../components/ui/app-select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const TYPES = [
  { value: "college", label: "College" },
  { value: "school", label: "School" },
  { value: "university", label: "University" },
  { value: "hospital", label: "Hospital" },
  { value: "other", label: "Other" },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.value, t.label]));
const EMPTY_FORM = { name: "", short_code: "", type: "college", is_active: true };

function StatusPill({ active }) {
  return (
    <span className={`event-status ${active ? "status-closed" : ""}`}>
      <i />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function RowMenu({ institution, onEdit, onDelete }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="row-menu-trigger"
          aria-label={`Actions for ${institution.name}`}
          data-testid={`institution-menu-${institution.id}`}
        >
          <Ellipsis size={18} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[150px]">
        <DropdownMenuItem
          data-testid={`edit-institution-button-${institution.id}`}
          onSelect={() => onEdit(institution)}
        >
          <Pencil size={15} />
          Edit details
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={`delete-institution-button-${institution.id}`}
          className="text-red-600 focus:text-red-700"
          onSelect={() => onDelete(institution)}
        >
          <Trash2 size={15} />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Institutions() {

  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  // Shared, cached data: opening this page again is instant, and a save or
  // delete below refreshes it automatically.
  const { data: institutions = [], isPending: loading, isError, error: loadError } = useInstitutions();
  const { data: events = [] } = useEvents();

  useEffect(() => {
    if (isError) {
      toast.error("Failed to load institutions", {
        description: loadError?.response?.data?.detail || "Please try again",
      });
    }
  }, [isError, loadError]);

  const eventCounts = useMemo(() => {
    const counts = {};
    events.forEach((event) => {
      counts[event.institution_id] = (counts[event.institution_id] || 0) + 1;
    });
    return counts;
  }, [events]);

  const openDialog = (institution = null) => {
    setEditing(institution);
    setForm(
      institution
        ? {
            name: institution.name,
            short_code: institution.short_code || "",
            type: institution.type || "college",
            is_active: institution.is_active,
          }
        : EMPTY_FORM,
    );
    setDialogOpen(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please enter the institution name");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/institutions/${editing.id}`, form);
        toast.success("Institution updated");
      } else {
        await api.post("/institutions", form);
        toast.success("Institution added");
      }
      setDialogOpen(false);
    } catch (error) {
      toast.error("Failed to save institution", {
        description: error.response?.data?.detail || "Please try again",
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/institutions/${pendingDelete.id}`);
      toast.success("Institution deleted");
      setPendingDelete(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error("Failed to delete institution", {
        description:
          typeof detail === "string"
            ? detail
            : "Institutions with linked events cannot be deleted",
      });
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return [...institutions]
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter(
        (inst) =>
          (type === "all" || (inst.type || "other") === type) &&
          (status === "all" || inst.is_active === (status === "active")) &&
          (!query ||
            inst.name.toLowerCase().includes(query) ||
            inst.short_code?.toLowerCase().includes(query)),
      );
  }, [institutions, search, type, status]);

  const visible = filtered;
  const hasFilters = search || type !== "all" || status !== "all";
  const clearFilters = () => {
    setSearch("");
    setType("all");
    setStatus("all");
  };

  const activeCount = institutions.filter((inst) => inst.is_active).length;
  const linkedEvents = institutions.reduce(
    (sum, inst) => sum + (eventCounts[inst.id] || 0),
    0,
  );

  const row = (institution) => (
    <>
      <div className="event-name-cell">
        <InstitutionLogo institution={institution} size={38} />
        <div>
          <button
            type="button"
            className="inst-name"
            onClick={() => openDialog(institution)}
          >
            {institution.name}
          </button>
          <span>{institution.short_code || "No short code"}</span>
        </div>
      </div>
    </>
  );

  return (
    <Layout>
      <div className="dashboard-page institutions-page">
        <div className="dashboard-heading">
          <div>
            <p className="page-eyebrow">
              {new Date().toLocaleDateString("en-GB", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </p>
            <h1>Institutions</h1>
            <p>The clients and campuses your studio shoots for.</p>
          </div>
          <div className="dashboard-heading-actions">
            <Button
              data-testid="create-institution-button"
              onClick={() => openDialog()}
            >
              <Plus size={17} />
              Add institution
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="dashboard-loading" role="status">
            <div className="brand-spinner" />
            <h2>Loading institutions</h2>
            <p>Fetching your client list.</p>
          </div>
        ) : (
          <>
            <div className="summary-strip" aria-label="Institution summary">
              <span>
                <Building2 size={16} />
                <strong>{institutions.length}</strong> Institutions
              </span>
              <span>
                <i className="strip-dot" />
                <strong>{activeCount}</strong> Active
              </span>
              <span>
                <CalendarDays size={16} />
                <strong>{linkedEvents}</strong> Events linked
              </span>
            </div>

            <section
              className="dashboard-panel inst-panel"
              aria-labelledby="inst-list-title"
            >
              <div className="panel-heading inst-panel-heading">
                <div className="inst-title-group">
                  <h2 id="inst-list-title">
                    All institutions{" "}
                    <span className="count-pill">{filtered.length}</span>
                  </h2>
                  <AppSelect
                    aria-label="Filter by type"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    options={[{ value: "all", label: "All types" }, ...TYPES]}
                  />
                  <AppSelect
                    aria-label="Filter by status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    options={[
                      { value: "all", label: "Any status" },
                      { value: "active", label: "Active" },
                      { value: "inactive", label: "Inactive" },
                    ]}
                  />
                </div>
                <div className="event-search inst-search">
                  <Search size={18} aria-hidden="true" />
                  <input
                    aria-label="Search institutions"
                    type="search"
                    placeholder="Search by name or code"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                    }}
                  />
                  {search && (
                    <button
                      onClick={() => {
                        setSearch("");
                      }}
                      aria-label="Clear search"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              {visible.length ? (
                <>
                  <div className="recent-table-scroll">
                    <table className="recent-table inst-table">
                      <caption className="sr-only">
                        Institutions, alphabetical
                      </caption>
                      <thead>
                        <tr>
                          <th scope="col">Institution</th>
                          <th scope="col">Type</th>
                          <th scope="col">Events</th>
                          <th scope="col">Status</th>
                          <th scope="col">
                            <span className="sr-only">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visible.map((institution) => (
                          <tr
                            key={institution.id}
                            data-testid={`institution-row-${institution.id}`}
                          >
                            <td>{row(institution)}</td>
                            <td className="event-date">
                              {TYPE_LABEL[institution.type] || "Other"}
                            </td>
                            <td className="event-date">
                              <span className="inst-events">
                                <CalendarDays size={14} />
                                {eventCounts[institution.id] || 0}
                              </span>
                            </td>
                            <td>
                              <StatusPill active={institution.is_active} />
                            </td>
                            <td className="inst-actions">
                              <RowMenu
                                institution={institution}
                                onEdit={openDialog}
                                onDelete={setPendingDelete}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <ul className="mobile-events" aria-label="Institutions">
                    {visible.map((institution) => (
                      <li key={institution.id}>
                        <div className="mobile-inst-row">
                          {row(institution)}
                          <RowMenu
                            institution={institution}
                            onEdit={openDialog}
                            onDelete={setPendingDelete}
                          />
                        </div>
                        <div>
                          <span>
                            {TYPE_LABEL[institution.type] || "Other"} ·{" "}
                            {eventCounts[institution.id] || 0} events
                          </span>
                          <StatusPill active={institution.is_active} />
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="dashboard-empty">
                  <Building2 size={28} />
                  <h3>
                    {hasFilters ? "No matching institutions" : "No institutions yet"}
                  </h3>
                  <p>
                    {hasFilters
                      ? "Try a different search or clear your filters."
                      : "Add the first institution to start scheduling events."}
                  </p>
                  {hasFilters ? (
                    <Button variant="outline" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  ) : (
                    <Button onClick={() => openDialog()}>Add institution</Button>
                  )}
                </div>
              )}

              <div className="table-footer">
                <span role="status" aria-live="polite">
                  {filtered.length === institutions.length
                    ? `${filtered.length} institutions`
                    : `${filtered.length} of ${institutions.length} institutions`}
                </span>
              </div>
            </section>
          </>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete institution?"
        description={`"${pendingDelete?.name}" will be removed. This fails if any events are linked to it.`}
        onConfirm={remove}
        confirmText="Delete institution"
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-slate-900">
                {editing ? "Edit institution" : "Add institution"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-5">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  data-testid="institution-name-input"
                  placeholder="Sri Manakula Vinayagar Engineering College"
                  className="mt-2"
                  autoFocus
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="short_code">Short code</Label>
                  <Input
                    id="short_code"
                    placeholder="SMVEC"
                    className="mt-2 uppercase"
                    maxLength={10}
                    value={form.short_code}
                    onChange={(e) =>
                      setForm({ ...form, short_code: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select
                    data-testid="institution-type-select"
                    value={form.type}
                    onValueChange={(value) => setForm({ ...form, type: value })}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label className="active-toggle" htmlFor="is_active">
                <input
                  id="is_active"
                  data-testid="institution-active-toggle"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm({ ...form, is_active: e.target.checked })
                  }
                />
                <span>
                  <strong>Active</strong>
                  <small>Switch off for clients you no longer work with.</small>
                </span>
              </label>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving…" : editing ? "Save changes" : "Add institution"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
